<template>
    <modal show-on-mount size="large" @hidden="hide">
        <template #modal-title>
            <h3>{{ resource('JourneyBuilder_Sankey_TouchpointOverviewPopup_Statistics') }} {{ brick.name }}</h3>
        </template>
        
        <template #modal-body>
            <div class="form-group">
                <div class="row">
                    <div class="col-12">
                        <a class="btn btn-outline-secondary mr-3" :class="{ disabled: !navigateToDashboardUrl }" :href="navigateToDashboardUrl || '#'">
                            {{resource('JourneyBuilder_Sankey_TouchpointInfo_OpenDashboard_Button')}}
                        </a>
                        
                        <navigate-to-actionable-report-link 
                            class="btn btn-outline-secondary"
                            
                            :journey-id="journey.id"
                            :highlighted-brick-id="brick.id"
                        />
                    </div>
                </div>
            </div>
            
            <div class="form-group">
                <div class="row">
                    <div class="col-6">
                        <div class="row">
                            <div class="col-4">
                                <label class="control-label">{{resource('JourneyBuilder_Sankey_Period_Label')}}</label>
                            </div>
                            
                            <div class="col-8">
                                <period-picker disabled />
                            </div>
                        </div>
                    </div>
                    
                    <div class="col-6">
                        <div class="row">
                            <div class="col-4">
                                <label class="control-label">
                                    {{resource('JourneyBuilder_Sankey_InflowPeriod_Label')}}
                                </label>
                            </div>
                            
                            <div class="col-8">
                                <inflow-period-picker disabled />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            
            <div class="form-group">
                <div class="row">
                    <div class="col-12">
                        <h3>{{ resource('JourneyBuilder_Sankey_TouchpointOverviewPopup_Reporting') }}</h3>
                    </div>
                </div>
            </div>
            
            <div class="form-group">
                <div class="row">
                    <div class="col-12">
                        <sankey-v2
                            :data="selectedBrickSankeyData"
                            :types="sankeyTypes"

                            :is-loading="isLoading"
                            :displayGoalRoutes="false"
                            
                            :selectedBrickId="brick.id"
                        />      
                    </div>
                </div>
            </div>
        </template>

        <template #modal-footer>
            <div class="row justify-content-end">
                <div class="col-auto">
                    <button class="btn btn-secondary btn-lg" @click="hide()">
                        <i class="fas fa-check-circle"/>&nbsp;&nbsp;{{resource('Button_OK')}}
                    </button>
                </div>
            </div>
        </template>
    </modal>
</template>

<script>
import { mapState, mapGetters, mapMutations, mapActions } from 'vuex';

import api from "@modules/JourneyBuilder/api";
import { DashboardUrlBuilder } from "@core/Util/dashboard-filter";
import { bootstrapDangerGrowl } from "@core/Util/growler";
import { buildTree } from "@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-tree";

import resourceMixin from "@core/Util/Mixins/resources";
import injectJourneyMixin from '@modules/JourneyBuilder/Mixins/inject-journey';

import Modal from '@core/Components/Util/Modal'
import SankeyV2, { PeriodPicker, InflowPeriodPicker } from "@modules/JourneyBuilder/Components/Charts/SankeyV2";
import { NavigateToActionableReportLink } from "@modules/JourneyBuilder/Components/Elements";
import SettingsProperty from '@modules/JourneyBuilder/Components/Composer/CanvasArea/Bricks/Settings/SettingsProperty';

export default {
    name: 'touchpoint-overview-popup',
    
    components: {
        Modal,
        SankeyV2,
        PeriodPicker,
        InflowPeriodPicker,
        NavigateToActionableReportLink,
        SettingsProperty
    },
    
    mixins: [injectJourneyMixin, resourceMixin],
    
    props: {
        /** @type {JourneyBrick} */
        brick: {
            type: Object,
            required: true
        }
    },
    
    data() {
        return {
            isLoading: false,

            /**  @type { SankeyTouchpointInfoConfig } */
            touchpointInfoConfig: undefined
        }
    },
    
    computed: {
        ...mapState('journeyBuilder/reporting', [ 'sankeyTypes', 'periodStart', 'periodEnd' ]),
        ...mapGetters('journeyBuilder/reporting', [ 'watchableReportStatisticsParameters', 'canLoadSankeyData', 'activeSankeyData' ]),

        navigateToDashboardUrl() {
            if(!this.touchpointInfoConfig) {
                return undefined;
            }
            
            const { dashboard } = this.touchpointInfoConfig;
            
            if(!dashboard.dashboardId) {
                return undefined;
            }
            
            return new DashboardUrlBuilder(dashboard.dashboardId)
                .withDashboardFilter(DashboardUrlBuilder.FILTER_START_DATE, this.periodStart)
                .withDashboardFilter(DashboardUrlBuilder.FILTER_END_DATE, this.periodEnd)
                .withDashboardFilter(dashboard.touchpointFilterId, this.brick.id)
                .getUrl();
        },
        
        selectedBrickSankeyData() {
            const tree = buildTree(this.activeSankeyData);

            const renderedNodeIds = [];

            /**
             * Recursively register the branch and complete child node graph
             * @param {JourneyBuilder.Sankey.TreeBranchV2} branch
             */
            const registerBranchAndChildrenForRendering = branch => {
                renderedNodeIds.push(branch.node.id);
                branch.children.forEach(child => registerBranchAndChildrenForRendering(child));
            }

            /**
             * Register the complete parent node graph
             * @param {JourneyBuilder.Sankey.TreeBranchV2} branch
             */
            const registerBranchForRendering = branch => {
                //First find the root node
                const seenBranchIds = [];

                while(branch.parent) {
                    if(seenBranchIds.includes(branch.parent.node.id)) {
                        break;
                    }

                    seenBranchIds.push(branch.parent.node.id);
                    branch = branch.parent;
                }

                registerBranchAndChildrenForRendering(branch);
            }

            //We only want to display paths containing the selected node
            tree.getBranchGroupByBrickId(this.brick.id)
                .forEach(branch => registerBranchForRendering(branch));

            return this.activeSankeyData.filter(d => renderedNodeIds.includes(d.id));
        }
    },
    
    watch: {
        async watchableReportStatisticsParameters() {
            this.isLoading = true;

            //Allow UI to update to display our loader
            await this.$nextTick();

            //Certain parameters will become available at a later stage - prevent wasting resources and stall by displaying our loader
            //while waiting for those parameters to become available
            if(!this.canLoadSankeyData) {
                return;
            }
            
            try {
                //Start off with all our sankey data
                await this.loadSankeyData();
            } catch (e) {
                bootstrapDangerGrowl(e);
            } finally {
                this.isLoading = false;
            }
        }
    },
    
    async created() {
        //Should be trigger our watchableReportStatisticsParameters watcher
        this.setJourneyId(this.journey.id);
        
        try {
            this.touchpointInfoConfig = await api.getBrickStatisticsConfiguration(this.journey.id, this.brick.id);
        } catch (e) {
            bootstrapDangerGrowl(e);
        }
    },
    
    beforeDestroy() {
        //Reset our store as to not accidentally render incorrect data when this component is opened for another brick
        this.setSankeyData([]);
        this.setSankeyTypes([]);
        
        this.setJourneyId(undefined);
        this.setPeriodStart(undefined);
        this.setPeriodEnd(undefined);
    },

    methods: {
        ...mapMutations('journeyBuilder/reporting', [
            'setSankeyData',
            'setSankeyTypes',
            
            'setJourneyId',
            'setPeriodStart', 
            'setPeriodEnd'
        ]),
        ...mapActions('journeyBuilder/reporting', [ 'loadSankeyData' ]),
        
        hide(){
            this.$emit('hide');
        }
    }
}
</script>

<style scoped lang="scss">
    ::v-deep {
        .sankey-v2 {
            border: none;
            
            //Make chart take full size of modal
            .chart {
                width: 100%;
                grid: auto-flow / calc(100%);
                padding-left: 0;
                padding-right: 0;

                //Hide chart controls/tags
                .type-legend,
                .touchpoint-legend,
                .tags-container,
                .depth-controls
                {
                    display: none;
                }

                //Hide any nodes rendered outside scroll container
                //Make scroll container full width
                .chart-scroll-container {
                    overflow-x: hidden;
                    grid: initial;
                }
            }
        }
    }
</style>

<style scoped lang="scss">
    @import "~@modules/JourneyBuilder/Components/Charts/SankeyV2/chart-highlighting.scss";

    @include enable-highlight;
    @include enable-link-hover-highlight;
</style>